<script setup lang="ts">
import { ref } from 'vue'
import { i18n } from '@/i18n'

const t = i18n.global.t

defineProps<{
  memorablePassword: string
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const copied = ref(false)

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(() => {
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  })
}

function close() {
  emit('update:modelValue', false)
}
</script>

<template>
  <div
    v-if="modelValue"
    class="absolute w-full h-full p-20 bg-black/70 z-20 font-alexandria flex flex-col items-center justify-start"
  >
    <div class="bg-white rounded-2xl flex flex-col p-8 max-w-2xl">
      <h1 class="text-3xl font-bold text-green">{{ t('accountCreated') }}</h1>
      <p class="text-md pt-3">{{ t('memorablePasswordIntro') }}</p>

      <!-- Memorable password display with copy functionality -->
      <div class="mt-6 p-6 bg-green/10 rounded-lg border-2 border-green">
        <p class="text-sm text-grey mb-2">{{ t('yourMemorablePassword') }}:</p>
        <div class="flex items-center justify-between">
          <p class="text-4xl font-bold text-green tracking-wide">{{ memorablePassword }}</p>
          <button
            @click="copyToClipboard(memorablePassword)"
            class="ml-4 px-4 py-2 bg-green text-white rounded-lg hover:bg-green/80 transition-colors"
          >
            {{ copied ? t('copied') : t('copy') }}
          </button>
        </div>
      </div>

      <!-- Security warnings for password care -->
      <div class="mt-6 p-4 bg-textYellow/20 rounded-lg border border-textYellow">
        <p class="text-sm font-semibold">⚠️ {{ t('memorablePasswordWarning') }}</p>
        <ul class="mt-2 text-sm list-disc list-inside space-y-1">
          <li>{{ t('memorablePasswordWarning1') }}</li>
          <li>{{ t('memorablePasswordWarning2') }}</li>
          <li>{{ t('memorablePasswordWarning3') }}</li>
        </ul>
      </div>

      <!-- Instructions for future login using this password -->
      <div class="mt-6">
        <p class="text-sm font-semibold mb-2">{{ t('howToUseMemorablePassword') }}:</p>
        <ol class="text-sm list-decimal list-inside space-y-1 text-grey">
          <li>{{ t('memorablePasswordStep1') }}</li>
          <li>{{ t('memorablePasswordStep2') }}</li>
          <li>{{ t('memorablePasswordStep3') }}</li>
        </ol>
      </div>

      <!-- Close Button -->
      <div class="flex flex-row pt-6 place-self-end">
        <button
          class="px-6 py-3 rounded-lg bg-green text-white cursor-pointer hover:bg-green/80 transition-colors font-semibold"
          @click="close"
        >
          {{ t('iHaveSavedIt') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
